import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Pencil, Trash2, ExternalLink, AlertCircle, Lock, Unlock, MessageSquare } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import NieuweVacatureModal from "../components/VacatureModal";
import ActivityLogModal from "../components/ActivityLogModal";
import Layout from "../components/Layout";
import { useChatWidget } from "../components/ChatWidget";
import { Button } from "../components/ui/button";
import { Pill } from "../components/ui/pill";
import { SectionLabel } from "../components/ui/section-label";
import { EmptyState } from "../components/ui/empty-state";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { StatusBadge, PriorityBadge, BemiddelingBadge, STATUS_LABELS } from "../components/VacatureBadges";
import { getVacature, updateVacature, deleteVacature, bullhornSearchJobOrders, resolveChat, BullhornJobOrder, ActivityLog } from "../api/api";
import { useBranches, useVacatureActivity, usePartners } from "../hooks/queries";
import { useAuth } from "../hooks/useAuth";
import { activityLabel, fmtDate, ERVARING_LABELS, isReviewedAtOnlyUpdate } from "../lib/activityLog";
import { formatSalaryIndication } from "../lib/salary";
import { toast } from "../lib/toast";
import { cn } from "@/lib/utils";

function daysSince(iso: string | null | undefined): number {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

function Card({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn("rounded-card border border-waves-line bg-white p-6", className)}>{children}</div>;
}

function DetailRow({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-4 border-t border-status-neutral-bg py-2.5 sm:grid-cols-[180px_1fr]">
      <div className="text-sm text-waves-muted">{k}</div>
      <div className="min-w-0 break-words text-sm font-medium">{v}</div>
    </div>
  );
}

function MetaRow({ k, v, tour }: { k: string; v: React.ReactNode; tour?: string }) {
  return (
    <div data-tour={tour} className="flex justify-between gap-3 border-t border-status-neutral-bg py-2.5">
      <span className="text-sm text-waves-muted">{k}</span>
      <span className="font-mono text-[13px] font-semibold">{v}</span>
    </div>
  );
}

export default function VacatureDetail() {
  const { vacatureId } = useParams<{ vacatureId: string }>();
  const id = Number(vacatureId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { openChat } = useChatWidget();
  const isWaves = user?.role === "admin" || user?.role === "kam";

  const { data: vacature, isLoading } = useQuery({
    queryKey: ["vacature", id],
    queryFn: () => getVacature(id),
    enabled: !!id,
  });

  const { data: activityLogs } = useVacatureActivity(id || undefined);
  const { data: branches } = useBranches(vacature?.partner_id ?? undefined, !!vacature?.partner_id);
  const branch = (branches ?? []).find(t => t.id === vacature?.branch_id);

  const { data: partners } = usePartners(!!vacature);
  const partner = partners?.find(p => p.id === vacature?.partner_id);
  const partnerName = partner?.name ?? "";

  const { data: vacatureChat } = useQuery({
    queryKey: ["chat-resolve", "vacature", id],
    queryFn: () => resolveChat({ partner_id: vacature!.partner_id!, job_opening_id: id }),
    enabled: !!vacature?.partner_id,
  });

  const bullhornQuery = vacature && partnerName
    ? [
        `isDeleted:0 AND NOT status:Archive`,
        `clientCorporation.name : "${partnerName}"`,
        `title:"*${vacature.functie}*"`,
        vacature.locatie ? `address.city:"${vacature.locatie}"` : null,
      ].filter(Boolean).join(" AND ")
    : "";

  const { data: bullhornData, isLoading: bullhornLoading, isError: bullhornError } = useQuery({
    queryKey: ["bullhorn-joborders", bullhornQuery],
    queryFn: () => bullhornSearchJobOrders(bullhornQuery, { silenceErrors: true }),
    enabled: !!(bullhornQuery && isWaves),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const [confirming, setConfirming] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["vacature", id] });
    queryClient.invalidateQueries({ queryKey: ["vacatures"] });
    queryClient.invalidateQueries({ queryKey: ["vacature-activity", id] });
  }

  async function handleBevestig() {
    setConfirming(true);
    try {
      await updateVacature(id, { reviewed_at: new Date().toISOString() });
      invalidate();
      toast("Vacature bevestigd als actueel", { kind: "success" });
    } finally {
      setConfirming(false);
    }
  }

  async function setStatus(status: string) {
    await updateVacature(id, { status });
    invalidate();
  }

  async function handleToggleStatus() {
    if (!vacature) return;
    const previous = vacature.status;
    const next = previous === "gesloten" ? "open" : "gesloten";
    setToggling(true);
    try {
      await setStatus(next);
      toast(next === "gesloten" ? "Vacature gesloten" : "Vacature heropend", {
        kind: "success",
        action: { label: "Ongedaan maken", onClick: () => void setStatus(previous) },
      });
    } finally {
      setToggling(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteVacature(id);
      queryClient.invalidateQueries({ queryKey: ["vacatures"] });
      toast("Vacature verwijderd", { kind: "success" });
      navigate(-1);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }

  const canEdit = user?.role === "admin" || user?.role === "kam" || user?.role === "hmp" || user?.role === "partner_admin";

  const backAction = (
    <Button variant="ghost" onClick={() => navigate(-1)} className="px-3">
      <ArrowLeft /> <span className="hidden sm:inline">Terug</span>
    </Button>
  );

  if (isLoading) {
    return (
      <Layout title="Vacature laden…" actions={backAction}>
        <div className="py-16 text-center text-sm text-waves-muted">Laden…</div>
      </Layout>
    );
  }

  if (!vacature) {
    return (
      <Layout title="Niet gevonden" actions={backAction}>
        <EmptyState title="Vacature niet gevonden" description="Deze vacature bestaat niet (meer) of je hebt er geen toegang toe." className="rounded-card border border-waves-line" />
      </Layout>
    );
  }

  const staledays = daysSince(vacature.reviewed_at ?? vacature.updated_at);
  const isStale = staledays > 14;
  const subtitle = [branch?.name ?? vacature.locatie, isWaves ? partnerName : null, `#${vacature.id}`].filter(Boolean).join(" · ");

  const timeline = [
    // Historic "status_change" rows (no longer written) are skipped: the
    // "update" row next to each one already shows the status change.
    ...(activityLogs ?? []).filter(log => log.action !== "status_change" && !isReviewedAtOnlyUpdate(log.action, log.payload)).map(log => ({
      key: `log-${log.id}`,
      date: fmtDate(log.created_at),
      who: log.actor_email,
      what: activityLabel(log.action, log.payload, "job_openings"),
      dot: log.action === "delete" ? "bg-status-alert-fg" : log.action === "create" ? "bg-waves-ink" : "bg-waves-zee",
      log,
    })),
    ...(isStale
      ? [{ key: "stale", date: "automatisch", who: "systeem", what: `Bevestiging vereist — ${staledays} dagen niet bevestigd`, dot: "bg-status-warn-fg", log: null as ActivityLog | null }]
      : []),
  ];

  return (
    <>
      <Layout title={vacature.functie} subtitle={subtitle} actions={backAction}>
        <div className="mx-auto flex max-w-[1240px] flex-col gap-5 lg:flex-row lg:items-start">
          {/* ── Main column ── */}
          <div className="flex min-w-0 flex-1 flex-col gap-5">
            <div data-tour="detail-badges" className="flex flex-wrap items-center gap-2.5 rounded-card border border-waves-line bg-white px-6 py-4">
              <StatusBadge value={vacature.status} />
              <PriorityBadge value={vacature.priority} />
              <BemiddelingBadge value={vacature.bemiddeling_status} />
              {isStale && <Pill kind="hold">{staledays} dagen niet bevestigd</Pill>}
              <span className="text-[13px] text-waves-muted sm:ml-auto">
                Aangemaakt {fmtDate(vacature.created_at)} · laatst bevestigd {vacature.reviewed_at ? (staledays === 0 ? "vandaag" : `${staledays} dagen geleden`) : "nooit"}
              </span>
            </div>

            <Card>
              <div className="pb-2 text-base font-bold">Functiedetails</div>
              <DetailRow k="Functietitel" v={vacature.functie || "—"} />
              <DetailRow k="Vestiging" v={branch ? branch.name : "—"} />
              <DetailRow k="Afdeling" v={vacature.department || "—"} />
              <DetailRow k="Locatie" v={vacature.locatie || "—"} />
              <DetailRow k="Ervaring" v={ERVARING_LABELS[vacature.ervaring] ?? vacature.ervaring ?? "—"} />
              <DetailRow k="Discipline" v={vacature.disciplines?.length ? vacature.disciplines.join(", ") : "—"} />
              <DetailRow k="Aantal posities" v={vacature.aantal_posities} />
              <DetailRow k="Salarisindicatie" v={formatSalaryIndication(vacature)} />
              <DetailRow k="Contactpersoon" v={vacature.contactpersoon || "—"} />
              <DetailRow k="Hiring manager" v={vacature.hiring_manager || "—"} />
              <DetailRow k="Aangemaakt door" v={vacature.created_by || "—"} />
              {vacature.opmerkingen && (
                <div className="mt-2 whitespace-pre-line border-t border-status-neutral-bg pt-4 text-sm leading-relaxed text-waves-body">
                  {vacature.opmerkingen}
                </div>
              )}
            </Card>

            <Card className="flex flex-col gap-1">
              <div className="pb-3 text-base font-bold">Verloop</div>
              {timeline.length === 0 && <p className="text-sm text-waves-muted">Nog geen activiteit.</p>}
              {timeline.map(e => {
                const clickable = !!e.log && e.log.action !== "confirmed";
                return (
                  <div
                    key={e.key}
                    role={clickable ? "button" : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onClick={clickable ? () => setSelectedLog(e.log) : undefined}
                    onKeyDown={clickable ? ev => { if (ev.key === "Enter") setSelectedLog(e.log); } : undefined}
                    className={cn("-mx-3 flex items-start gap-3 rounded-nav px-3 py-2", clickable && "cursor-pointer hover:bg-waves-subtle")}
                  >
                    <span className={cn("mt-[7px] h-2 w-2 shrink-0 rounded-full", e.dot)} />
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <div className="font-mono text-xs text-waves-muted">{e.date}</div>
                      <div className="break-words text-sm"><b>{e.who}</b> — {e.what}</div>
                    </div>
                  </div>
                );
              })}
            </Card>
          </div>

          {/* ── Side column ── */}
          <div className="flex w-full shrink-0 flex-col gap-5 lg:w-[300px]">
            {canEdit && (
              <Card className="flex flex-col gap-2.5">
                <SectionLabel className="pb-1">Acties</SectionLabel>
                <Button size="lg" data-tour="btn-confirm" className="h-12 text-[15px]" onClick={handleBevestig} disabled={confirming}>
                  <Check /> Bevestig actueel
                </Button>
                <Button variant="outline" data-tour="btn-edit" className="h-12 text-[15px]" onClick={() => setShowEdit(true)}>
                  <Pencil /> Bewerken
                </Button>
                <Button variant="outline" data-tour="btn-close" className="h-12 text-[15px]" onClick={handleToggleStatus} disabled={toggling}>
                  {vacature.status === "gesloten" ? <><Unlock /> Vacature heropenen</> : <><Lock /> Vacature sluiten</>}
                </Button>
                <Button variant="destructive" data-tour="btn-delete" className="h-12 text-[15px]" onClick={() => setShowDeleteConfirm(true)} disabled={deleting}>
                  <Trash2 /> Verwijder vacature
                </Button>
              </Card>
            )}

            {vacatureChat && (
              <Card className="flex flex-col gap-3">
                <SectionLabel>Chat</SectionLabel>
                <p className="text-sm text-waves-body">Vragen over deze vacature? Stel ze direct in de chat van deze vacature.</p>
                <Button variant="ink" onClick={() => openChat(vacatureChat)}>
                  <MessageSquare /> Open chat over deze vacature
                </Button>
              </Card>
            )}

            {isWaves && (
              <div className="overflow-hidden rounded-card border border-waves-line bg-white">
                <div className="flex items-center gap-2 px-6 pb-3 pt-5">
                  <SectionLabel className="flex-1">Bullhorn</SectionLabel>
                  {bullhornData && <span className="font-mono text-[11px] text-waves-muted">{bullhornData.total} resultaten</span>}
                </div>

                {bullhornLoading && (
                  <div className="flex flex-col gap-2 px-4 pb-4">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="rounded-nav border border-waves-line p-3">
                        <div className="mb-2 h-2.5 w-3/4 animate-pulse rounded bg-status-neutral-bg" />
                        <div className="h-2 w-1/2 animate-pulse rounded bg-status-neutral-bg" />
                      </div>
                    ))}
                  </div>
                )}

                {bullhornError && !bullhornLoading && (
                  <div className="flex items-center gap-2 border-t border-waves-line px-6 py-5 text-[13px] text-waves-body">
                    <AlertCircle size={14} className="shrink-0 text-status-warn-fg" />
                    Bullhorn niet bereikbaar
                  </div>
                )}

                {!bullhornLoading && !bullhornError && bullhornData && bullhornData.data.length === 0 && (
                  <div className="border-t border-waves-line px-6 py-5 text-center text-[13px] text-waves-muted">
                    Geen openstaande vacatures gevonden
                  </div>
                )}

                {!bullhornLoading && !bullhornError && bullhornData && bullhornData.data.length > 0 && (
                  <div className="divide-y divide-waves-line border-t border-waves-line">
                    {bullhornData.data.map((job: BullhornJobOrder) => (
                      <a
                        key={job.id}
                        href={`https://cls20.bullhornstaffing.com/BullhornSTAFFING/OpenWindow.cfm?Entity=JobOrder&id=${job.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-start justify-between gap-2 px-6 py-3 transition-colors hover:bg-waves-subtle"
                      >
                        <div className="min-w-0">
                          <div className="text-[13px] font-bold leading-snug group-hover:text-waves-accent">{job.title}</div>
                          <div className="mt-0.5 text-[11px] text-waves-muted">{job.correlatedCustomText10 || job.address?.city || "—"}</div>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <span className="font-mono text-[11px] text-waves-muted">#{job.id}</span>
                          <ExternalLink size={10} className="text-waves-muted" />
                        </div>
                      </a>
                    ))}
                  </div>
                )}

                {!bullhornLoading && !bullhornError && !bullhornData && (
                  <div className="border-t border-waves-line px-6 py-5 text-center text-[13px] text-waves-muted">Laden…</div>
                )}
              </div>
            )}

            <Card className="flex flex-col">
              <SectionLabel className="pb-2">Details</SectionLabel>
              <MetaRow k="ID" v={`#${vacature.id}`} />
              <MetaRow k="Status" v={STATUS_LABELS[vacature.status] ?? vacature.status} />
              <MetaRow k="Aangemaakt" v={fmtDate(vacature.created_at)} />
              <MetaRow k="Bijgewerkt" v={fmtDate(vacature.updated_at)} />
              <MetaRow k="Bevestigd" v={fmtDate(vacature.reviewed_at)} tour="detail-confirmed" />
            </Card>
          </div>
        </div>
      </Layout>

      {showEdit && vacature.partner_id && (
        <NieuweVacatureModal
          partnerId={vacature.partner_id}
          initialData={vacature}
          onClose={() => setShowEdit(false)}
          onCreated={invalidate}
        />
      )}

      <Dialog open={showDeleteConfirm} onOpenChange={o => { if (!deleting) setShowDeleteConfirm(o); }}>
        <DialogContent data-tour="delete-dialog">
          <DialogHeader>
            <DialogTitle>Vacature verwijderen?</DialogTitle>
            <DialogDescription>
              Je staat op het punt <b className="text-waves-ink">{vacature.functie}</b> permanent te verwijderen. Dit kan niet ongedaan worden gemaakt.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>Annuleren</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Verwijderen…" : "Verwijder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ActivityLogModal
        log={selectedLog}
        onClose={() => setSelectedLog(null)}
        branches={branches}
        partners={partners}
      />
    </>
  );
}
